import { Box } from "@mui/material";
import { useCallback, useMemo } from "react";
import { AvailableIntegration } from "~/pages/integrations/components/available-integrations/available-integration-cards-list/AvailableIntegrationCard";
import {
  AvailableIntegrationCardsList,
  IntegrationCardsListProps,
} from "~/pages/integrations/components/available-integrations/available-integration-cards-list/AvailableIntegrationCardsList";
import { motion } from "motion/react";
import { fadeInOut } from "~/lib/animations";
import { FilterBar } from "~/components/FilterBar/FilterBar";
import { useSearch } from "~/components/search/useSearch";
import { NoSearchResults } from "~/pages/Search";
import { AvailableIntegrationsRequestBanner } from "~/pages/integrations/components/available-integrations/AvailableIntegrationsRequestBanner";
import { Stretcher } from "~/components/Stretcher/Stretcher";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { checkFeatureEnabled } from "~/login/features";

interface AvailableIntegrationsProps {
  scope: SpaceScope | OrganizationScope;
}

export function AvailableIntegrations({ scope }: AvailableIntegrationsProps) {
  const search = useSearch();

  const filteredBy = useCallback(
    (
      { title, integrations }: AvailableIntegrationList,
      searchFilters: string[],
    ) => {
      if (searchFilters.length === 0) return integrations;

      return integrations.filter((integration) => {
        return searchFilters.every((filter) => {
          return [
            // Check if the group title matches the filter
            title,
            // Check the integration titles individually
            integration.title,
          ].some((title) =>
            title?.toLowerCase().includes(filter.toLowerCase()),
          );
        });
      });
    },
    [search.searchFilters],
  );

  const filteredIntegrationLists = useMemo(() => {
    return availableIntegrations.flatMap((ai) => {
      const key = `${ai.title}-integration`;
      const integrations = filteredBy(ai, search.searchFilters);
      return integrations.length > 0
        ? [
            <AvailableIntegrationCardsList
              key={key}
              scope={scope}
              title={ai.title}
              recommended={ai.recommended}
              integrations={integrations}
            />,
          ]
        : [];
    });
  }, [search.searchFilters]);

  return (
    <Box component={motion.div} {...fadeInOut} key="available-integrations">
      <FilterBar
        searchId="available-integrations"
        placeholder="available_integrations"
        search={search}
        mb={5}
      />

      <Box sx={{ mb: 24 }}>
        {filteredIntegrationLists.length > 0 && filteredIntegrationLists}
        {filteredIntegrationLists.length === 0 && (
          <NoSearchResults
            searchQuery={search.searchFilters.join(" + ")}
            name={scope.name}
          />
        )}
      </Box>

      {/* Bottom Banner */}
      <Stretcher>
        <AvailableIntegrationsRequestBanner />
      </Stretcher>
    </Box>
  );
}

// Integration Card Data
const recommendedIntegrations: AvailableIntegration[] = [
  {
    id: "mondoo/aws",
    title: "AWS",
    description:
      "Continuously scan AWS organizations, accounts, and instances for misconfigurations and vulnerabilities.",
  },
  {
    id: "mondoo/azure",
    title: "Azure",
    description:
      "Continuously scan Microsoft Azure subscriptions and resources for misconfigurations and vulnerabilities.",
  },
  {
    id: "mondoo/kubernetes",
    title: "Kubernetes",
    description:
      "Deploy the Mondoo Kubernetes Operator to enable continuous workload & node security scanning with optional scanning of new deployments.",
  },
];

const serverEndpointSecurityIntegrations: AvailableIntegration[] = [
  {
    id: "mondoo/linux",
    title: "Linux",
    description:
      "Deploy cnspec on your Linux hosts to continuously scan them for known vulnerabilities, misconfigurations, and policy compliance.",
    flags: null,
  },
  {
    id: "mondoo/macos",
    title: "macOS",
    description:
      "Deploy cnspec on your macOS hosts to continuously scan them for known vulnerabilities, misconfigurations, and policy compliance. ",
    flags: null,
  },
  {
    id: "mondoo/windows",
    title: "Windows",
    description:
      "Deploy cnspec on your Windows hosts to continuously scan them for known vulnerabilities, misconfigurations, and policy compliance. ",
    flags: null,
  },
];

const cloudSecurityIntegrations: AvailableIntegration[] = [
  {
    id: "mondoo/aws",
    title: "AWS",
    description:
      "Continuously scan AWS organizations, accounts, and instances for misconfigurations and vulnerabilities.",
    flags: null,
  },
  {
    id: "mondoo/azure",
    title: "Azure",
    description:
      "Continuously scan Microsoft Azure subscriptions and resources for misconfigurations and vulnerabilities.",
  },
  {
    id: "mondoo/gcp",
    title: "GCP",
    description:
      "Continuously scan Google GCP organizations and projects for misconfigurations and vulnerabilities.",
  },
  {
    id: "mondoo/kubernetes",
    title: "Kubernetes",
    description:
      "Deploy the Mondoo Kubernetes Operator to enable continuous workload & node security scanning with optional scanning of new deployments.",
    flags: null,
  },
  {
    id: "mondoo/vmware",
    title: "VMware",
    description:
      "Deploy the Mondoo VMware Appliance to scan VMware vCenter Server, vSphere API, ESXi, and virtual machines.",
    flags: null,
  },
  {
    id: "mondoo/oci",
    title: "Oracle Cloud Infrastructure",
    description:
      "Continuously scan Oracle Cloud Infrastructure (OCI) tenancies and resources for misconfigurations and vulnerabilities.",
    flags: null,
  },
];

const thirdPartyDataIntegrations: AvailableIntegration[] = [
  {
    id: "mondoo/defender",
    title: "Microsoft Defender for Cloud",
    description:
      "Import security findings and vulnerabilities discovered with Microsoft Defender for Cloud.",
    flags: null,
  },
  {
    id: "mondoo/sentinelone",
    title: "SentinelOne Singularity",
    description:
      "Import security findings discovered with SentinelOne Singularity platform.",
    flags: null,
  },
  {
    id: "mondoo/crowdstrike",
    title: "CrowdStrike Falcon",
    description:
      "Import security findings discovered by the CrowdStrike Falcon Spotlight exposure management tool.",
    flags: null,
  },
];

const softwareSupplyChainIntegrations: AvailableIntegration[] = [
  {
    id: "hashicorp/packer",
    title: "Packer",
    description:
      "Use the Mondoo HashiCorp Packer provisioner to scan for policy compliance and vulnerabilities during an image build.",
    flags: null,
  },
  {
    id: "hashicorp/terraform",
    title: "Terraform",
    description:
      "Use Mondoo to run a static analysis of HashiCorp Terraform (HCL) code before it's run, or run Mondoo as a post-provisioning step in your Terraform workflows.",
    flags: null,
  },
  {
    id: "mondoo/credentials",
    title: "Generate Long-Lived Credentials",
    description:
      "Integrate Mondoo into any custom automated workflow by generating a long-lived credential file.",
    flags: null,
  },
];

const saasIntegrations: AvailableIntegration[] = [
  {
    id: "mondoo/ms365",
    title: "Microsoft 365",
    description:
      "Continuously scan Microsoft 365 resources for security misconfigurations.",
    flags: null,
  },
  {
    id: "mondoo/okta",
    title: "Okta",
    description:
      "Continuously scan your Okta domain including users, groups, policies, rules, applications and more.",
    flags: null,
  },
  {
    id: "mondoo/google_workspace",
    title: "Google Workspace",
    description:
      "Continuously scan your Google Workspace environment for security misconfigurations.",
    flags: null,
  },
  {
    id: "mondoo/gitlab",
    title: "GitLab",
    description:
      "Continuously scan your GitLab groups for security misconfigurations",
  },
  {
    id: "mondoo/github",
    title: "GitHub",
    description:
      "Continuously scan your GitHub organizations and repositories for security misconfigurations",
  },
  {
    id: "mondoo/slack",
    title: "Slack",
    description:
      "Continuously scan your Slack Teams for security misconfigurations",
    flags: null,
  },
];

const networkSecurityIntegrations: AvailableIntegration[] = [
  {
    id: "mondoo/cisco",
    title: "Cisco",
    description:
      "Scan Cisco IOS XR, IOS XE, and NX-OS devices for security misconfigurations, vulnerabilities, and EOL status",
    flags: null,
  },
  {
    id: "mondoo/fortios",
    title: "Fortinet",
    description:
      "Scan Fortinet FortiOS devices for vulnerabilities and EOL status",
    flags: null,
  },
  {
    id: "mondoo/panos",
    title: "Palo Alto Networks",
    description:
      "Scan Palo Alto Networks PAN-OS devices for vulnerabilities and EOL status",
    flags: null,
  },
  {
    id: "mondoo/junos",
    title: "Juniper",
    description: "Scan Juniper Networks Junos devices for vulnerabilities",
    flags: null,
  },
  {
    id: "mondoo/host",
    title: "Domain/IP Address",
    description:
      "Continuously scan endpoints to evaluate domain TLS, SSL, HTTP, and HTTPS security",
    flags: null,
  },
  {
    id: "mondoo/shodan",
    title: "Shodan",
    description:
      "Continuously assess external risk for domains and IP addresses",
    flags: null,
  },
];

const ticketIntegrations: AvailableIntegration[] = [
  {
    id: "mondoo/ticket_system_jira",
    title: "Atlassian Jira",
    description:
      "To incorporate Mondoo into your Jira workflow, automatically create a Jira issue when you create a new ticket in the Mondoo Console.",
    flags: null,
  },
  {
    id: "mondoo/ticket_system_email",
    title: "Ticketing via email",
    description:
      "To incorporate Mondoo into your regular tracking workflow, send email to your external ticketing system when you create a new ticket in the Mondoo Console.",
    flags: null,
  },
  {
    id: "mondoo/ticket_system_zendesk",
    title: "Zendesk",
    description:
      "To incorporate Mondoo into your Zendesk workflow, automatically create a Zendesk ticket when you create a new ticket in the Mondoo Console.",
    flags: null,
  },
  {
    id: "mondoo/ticket_system_github",
    title: "GitHub Issues",
    description:
      "To incorporate Mondoo into your GitHub issue tracking workflow, automatically create a GitHub issue when you create a new ticket in the Mondoo Console.",
    flags: null,
  },
  {
    id: "mondoo/ticket_system_gitlab",
    title: "GitLab Issues",
    description:
      "To incorporate Mondoo into your GitLab issue tracking workflow, automatically create a GitLab issue when you create a new ticket in the Mondoo Console.",
    flags: null,
  },
  {
    id: "mondoo/ticket_system_azure_devops",
    title: "Azure DevOps",
    description:
      "To incorporate Mondoo into your Azure DevOps issue tracking workflow, automatically create an Azure DevOps issue when you create a new ticket in the Mondoo Console.",
    flags: null,
  },
  checkFeatureEnabled("ServiceNow Integration")
    ? {
        id: "mondoo/ticket_system_servicenow",
        title: "ServiceNow",
        description:
          "To incorporate Mondoo into your ServiceNow workflow, automatically create a ServiceNow incident when you create a new ticket in the Mondoo Console.",
        flags: null,
      }
    : null,
  {
    id: "mondoo/ticket_system_webhook",
    title: "Webhook",
    description:
      "To incorporate Mondoo into your external ticketing system, send JSON-encoded data to any HTTP endpoint when you create a new ticket in the Mondoo Console.",
    flags: null,
  },
].flatMap((x) => (x !== null ? [x] : []));

const cicdIntegrations: AvailableIntegration[] = [
  {
    id: "cicd/circleci",
    title: "CircleCI",
    description:
      "Prevent insecure changes from leaving your development environment by integrating Mondoo with your CirceCI projects.",
    flags: null,
  },
  {
    id: "cicd/github",
    title: "GitHub Actions",
    description:
      "Prevent insecure changes from leaving your development environment by integrating Mondoo with your GitHub Actions.",
    flags: null,
  },
  {
    id: "cicd/gitlab",
    title: "GitLab CI/CD",
    description:
      "Prevent insecure changes from leaving your development environment by integrating Mondoo with your GitLab CI/CD pipelines.",
    flags: null,
  },
  {
    id: "cicd/azure",
    title: "Azure Pipelines",
    description:
      "Prevent insecure changes from leaving your development environment by integrating Mondoo with your Azure Pipelines.",
    flags: null,
  },
  {
    id: "cicd/jenkins",
    title: "Jenkins",
    description:
      "Prevent insecure changes from leaving your development environment by integrating Mondoo with your Jenkins pipelines.",
    flags: null,
  },
];

const exportIntegrations: AvailableIntegration[] = [
  {
    id: "mondoo/bigquery",
    title: "BigQuery",
    description:
      "Continuously share scan results with Google BigQuery for storage, analysis, and reporting.",
    flags: null,
  },
  {
    id: "mondoo/snowflake",
    title: "Snowflake",
    description:
      "Continuously share scan results with Snowflake for storage, analysis, and reporting.",
    flags: null,
  },
  {
    id: "mondoo/amazons3",
    title: "Amazon S3",
    description: "Continuously export data to Amazon S3 in JSONL or CSV format",
    flags: null,
  },
  {
    id: "mondoo/amazon-s3-compatible",
    title: "S3 Compatible Service",
    description:
      "Continuously export data to Amazon S3-compatible services in JSONL or CSV format",
    flags: null,
  },
  {
    id: "mondoo/postgres",
    title: "PostgreSQL",
    description: "Continuously export data to a PostgreSQL database",
    flags: null,
  },
  {
    id: "mondoo/gcs_bucket",
    title: "GCP Cloud Storage Bucket",
    description:
      "Continuously export data to GCP Cloud Storage in JSONL or CSV format",
    flags: null,
  },
  {
    id: "mondoo/azure_blob",
    title: "Azure Blob Storage",
    description:
      "Continuously export data to Azure Blob Storage in JSONL or CSV format",
    flags: null,
  },
].flatMap((x) => (x !== null ? [x] : []));

const chatOpsIntegrations: AvailableIntegration[] = [
  {
    id: "chat/msteams",
    title: "Microsoft Teams",
    description:
      "Send alert messages and notifications from Mondoo directly to Microsoft Teams.",
    flags: null,
  },
  {
    id: "chat/slack",
    title: "Slack",
    description:
      "Send alert messages and notifications from Mondoo directly to a Slack channel.",
    flags: null,
  },
  {
    id: "chat/telegram",
    title: "Telegram",
    description:
      "Send alert messages and notifications from Mondoo directly to Telegram.",
    flags: null,
  },
  {
    id: "chat/httppost",
    title: "Outbound Webhook",
    description:
      "Send alert messages and notifications from Mondoo as JSON-encoded data to any HTTP endpoint.",
    flags: null,
  },
];

type AvailableIntegrationList = Pick<
  IntegrationCardsListProps,
  "title" | "recommended" | "integrations"
>;

const availableIntegrations: AvailableIntegrationList[] = [
  {
    recommended: true,
    integrations: recommendedIntegrations,
  },
  {
    title: "Server & Endpoint Security",
    integrations: serverEndpointSecurityIntegrations,
  },
  {
    title: "Cloud Security",
    integrations: cloudSecurityIntegrations,
  },
  {
    title: "Third-Party Data",
    integrations: thirdPartyDataIntegrations,
  },
  {
    title: "Supply Chain",
    integrations: softwareSupplyChainIntegrations,
  },
  {
    title: "SaaS",
    integrations: saasIntegrations,
  },
  {
    title: "Network Security",
    integrations: networkSecurityIntegrations,
  },
  {
    title: "CI/CD",
    integrations: cicdIntegrations,
  },
  {
    title: "Exports",
    integrations: exportIntegrations,
  },
  {
    title: "Ticket Systems",
    integrations: ticketIntegrations,
  },
  {
    title: "Chat Ops",
    integrations: chatOpsIntegrations,
  },
];
